/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore
} from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./checkout';
import { IEmailDelivery } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./components';
import { IInvoicePaymentSummary } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./components/get-invoice-payment-summary';
import { ICheckoutProps as ICheckoutExtensionProps } from '../definition-extensions/checkout.ext.props.autogenerated';
import { AttributeBooleanValue, Cart } from '@msdyn365-commerce/retail-proxy';
import { ICartState } from '@msdyn365-commerce/global-state';
import { addAttributeToCart, getAttributeByKey } from '../../../utils/cartAttributeManager';
import { IChargeLinesBreakdownData } from '../../../utils/chargeLinesHelper';

interface ChargeNodeProps {
    chargeName: string;
    chargeAmount: string;
    chargeCode?: string;
    cart?: Cart;
    updateCart?: ICartState;
    chargeLineToBeRemoved: string;
    description?: string;
}

interface EstimatedTaxOverrideValue {
    chargeCode: string;
    description: string;
    amount: number;
}

export interface ILineItemsExtended extends ILineItems {
    shippingState?: string;
}

export interface ILineItemDeliveryGroupExtended extends ILineItemDeliveryGroup {
    shippingState?: string;
}

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export interface IOrderSummaryExtended extends IOrderSummary {
    otherChargesLines: React.ReactNode[];
    displayDefaultShippingLine: boolean | undefined;
}

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem & { shippingState?: string }> = ({
    LineItem,
    item,
    pickUpAtStore,
    emailDelivery,
    shippingState
}) => {
    // Clone the item ReactNode and inject the checkoutShippingState prop
    const clonedItem =
        item && React.isValidElement(item)
            ? React.cloneElement(item as React.ReactElement, { checkoutShippingState: shippingState })
            : item;

    return (
        <Node {...LineItem}>
            {clonedItem}
            {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
            {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
        </Node>
    );
};

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroupExtended> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    shippingState
}) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} shippingState={shippingState} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItemsExtended> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode,
    shippingState
}) => {
    const items = itemsForPickup ? itemsForPickup : itemsForEmail ? itemsForEmail : itemsForShip;
    const lineItemGroupComponentExt: ILineItemDeliveryGroupExtended = { ...items!, shippingState };

    return (
        <Node {...LineItems}>
            <Node {...Header}>
                {heading}
                {editLink}
            </Node>
            {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...lineItemGroupComponentExt} />}
            {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...lineItemGroupComponentExt} />}
            {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...lineItemGroupComponentExt} />}
            {itemsGroupWithMulitplePickupMode !== undefined
                ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                      return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
                  })
                : null}
        </Node>
    );
};

const OrderSummaryComponent: React.FC<IOrderSummaryExtended> = ({ heading, lines, otherChargesLines, displayDefaultShippingLine }) => (
    <div className='msc-order-summary-wrapper'>
        {heading}
        <div className='msc-order-summary__items'>
            {lines && (
                <>
                    {lines.subtotal}
                    {displayDefaultShippingLine && lines.shipping}
                    {/* {lines.otherCharge} */}
                    {otherChargesLines && otherChargesLines}
                    {lines.tax}
                    {lines.totalDiscounts}
                    {lines.loyalty}
                    {lines.giftCard}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const otherChargesLines = (
    cart: Cart,
    chargeCodes: IChargeLinesBreakdownData[] | undefined,
    currencyFormatter: (price: string | number, currencyCode?: string | undefined) => string,
    displayDefaultShippingLine: boolean | undefined,
    updateCart: ICartState | undefined,
    chargeLineToBeRemoved: string
): React.ReactNode[] => {
    const cartLinesChargeLines = (cart?.CartLines?.map(item => Array.from(item.ChargeLines || [])) || []).flat();
    const allChargeLines = [...(cart?.ChargeLines || []), ...cartLinesChargeLines];

    let linesNode: React.ReactNode[] = [];
    let otherChargeTotal = cart.OtherChargeAmount || 0;

    const sumsByCode = allChargeLines.reduce<Record<string, number>>((acc, line) => {
        const code = line.ChargeCode || '';
        const amt = line.CalculatedAmount ?? 0;
        if (amt >= 0.01) {
            acc[code] = (acc[code] || 0) + amt;
        }
        return acc;
    }, {});

    chargeCodes?.forEach((c, idx) => {
        const amount = sumsByCode[c.chargeCode];
        if (amount && amount >= 0.01) {
            otherChargeTotal -= amount;
            linesNode.push(
                <RenderLineChargeNode
                    key={`${c.chargeCode}-${idx}`}
                    chargeName={c.chargeLineLabel ?? ''}
                    chargeAmount={currencyFormatter(amount)}
                    chargeCode={c.chargeCode}
                    cart={cart}
                    updateCart={updateCart}
                    chargeLineToBeRemoved={chargeLineToBeRemoved}
                    description={c.chargeLineLabel}
                />
            );
        }
    });
    // if the remaining amount is over 1 cent, display the other Charges line. This avoids potential fraction of a penny being displayed as zero.
    if (otherChargeTotal >= 0.01) {
        linesNode.push(
            <RenderLineChargeNode
                key='other-charges'
                chargeName='Other Charges'
                chargeAmount={currencyFormatter(otherChargeTotal)}
                cart={cart}
                updateCart={updateCart}
                chargeLineToBeRemoved={chargeLineToBeRemoved}
            />
        );
    }
    // if no charge exists, display shipping to be calculated text
    // if (cart.ChargeAmount === 0 && !displayDefaultShippingLine) {
    //     const shippingToBeDetermined = _renderLineChargeNode('Shipping', 'To be calculated');
    //     linesNode.push(shippingToBeDetermined);
    // }
    return linesNode;
};

const RenderLineChargeNode: React.FC<ChargeNodeProps> = ({
    chargeName,
    chargeAmount,
    chargeCode,
    cart,
    updateCart,
    chargeLineToBeRemoved,
    description
}) => {
    React.useEffect(() => {
        const syncEstimatedTaxOverride = async () => {
            if (chargeCode !== chargeLineToBeRemoved || !cart || !updateCart) {
                return;
            }

            const attrKey = 'EstimatedTaxOverride';
            const existingAttr = getAttributeByKey(cart, attrKey);

            const newValue: EstimatedTaxOverrideValue = {
                chargeCode,
                description: chargeName ?? description,
                amount: parseFloat(chargeAmount.replace(/[^0-9.-]+/g, '')) || 0
            };

            // see if existing value is the same
            let isSame = false;
            if (existingAttr?.TextValue) {
                try {
                    const parsed = JSON.parse(existingAttr.TextValue) as EstimatedTaxOverrideValue;
                    isSame =
                        parsed.chargeCode === newValue.chargeCode &&
                        parsed.description === newValue.description &&
                        Number(parsed.amount) === Number(newValue.amount);
                } catch {}
            }

            if (!isSame) {
                const serialized = JSON.stringify(newValue);
                const updatedCart = addAttributeToCart(attrKey, serialized, cart);

                try {
                    await updateCart.updateCart({ newCartObject: updatedCart });
                    await updateCart.refreshCart({});
                } catch (e) {
                    console.error('Failed to sync EstimatedTaxOverride:', e);
                }
            }
        };

        syncEstimatedTaxOverride();
    }, [chargeCode, chargeName, chargeAmount, cart, updateCart, chargeLineToBeRemoved]);

    return (
        <p className='msc-order-summary__line-other-charges'>
            <span className='msc-order-charge-line__label'>{chargeName}</span>{' '}
            <span className='msc-price msc-order-summary__value'>{chargeAmount}</span>
        </p>
    );
};

const _renderShipOnCheckout = (
    shipCompleteOnCheckout: boolean,
    handleShipCompleteOnCheckoutChange: (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => void,
    shipCompleteOnCheckoutLabel: string
): React.ReactNode => (
    <div className='ms-checkout__ship-on-checkout'>
        <label className='input-group-prepend print-view-toggle__label'>
            <span className={'print-view-toggle__heading'}>{shipCompleteOnCheckoutLabel}</span>
            <input
                value={shipCompleteOnCheckout ? 1 : 0}
                onClick={handleShipCompleteOnCheckoutChange}
                type='checkbox'
                className='print-view-toggle__checkbox'
            />
            <div className={`print-view-toggle__toggle print-view-toggle__toggle--${shipCompleteOnCheckout ? 'checked' : 'unchecked'}`} />
        </label>
    </div>
);

const _renderOrderComment = (commentHeading: string, commentString: string): React.ReactNode => {
    const emptyStringClassName = commentString == '';
    return (
        <div className={`order-level-comment-wrapper ${emptyStringClassName ? 'order-level-comment-wrapper--empty-string' : ''}`}>
            <h1 className='order-level-comment-wrapper__heading'>{commentHeading}</h1>
            <p className='order-level-comment-wrapper__text'>{commentString}</p>
        </div>
    );
};

const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutExtensionProps<{}>> = props => {
    const attributeArr = props.data.checkout.result?.checkoutCart.cart.AttributeValues || [];

    const getShipCompleteIndex = (): number => {
        return (
            props.data.checkout.result?.checkoutCart.cart.AttributeValues?.findIndex(attribute => attribute.Name === 'ShipComplete') || -1
        );
    };
    // Get the text in the order level comment, if it doesn't exist, assign an empty string
    const getCommentText = (): string => {
        return props.data.checkout.result?.checkoutCart.cart.Comment || '';
    };
    // Get the comment heading text from the resources
    const getCommentHeadingText = (): string => {
        return props.resources.commentHeadingText;
    };
    const getShipCompleteValue = (): boolean => {
        const shipCompleteIndex = getShipCompleteIndex();
        if (shipCompleteIndex === -1) {
            return false;
        } else {
            const attr = attributeArr[shipCompleteIndex] as AttributeBooleanValue;
            return !!attr?.Value;
        }
    };
    const handleShipCompleteOnCheckoutChange = () => {
        const shipComplete = {
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeBooleanValue',
            Name: 'ShipComplete',
            Value: !getShipCompleteValue()
        };
        const idx = getShipCompleteIndex();
        idx === -1 ? attributeArr.push(shipComplete) : attributeArr.splice(idx, 1, shipComplete);
        const cart: Cart = Object.assign({}, props.data.checkout.result?.checkoutCart.cart);
        if (cart) {
            cart.AttributeValues = attributeArr;
            saveShipCompleteToCart(cart);
        }
    };
    const saveShipCompleteToCart = async (cart: Cart) => {
        if (cart) {
            const checkoutState = props.data.checkout.result;
            if (!checkoutState) {
                return;
            }

            await checkoutState.checkoutCart.updateCart({ newCartObject: cart }).then(() => {
                setShipCompleteOnCheckout(getShipCompleteValue());
            });
        }
    };
    const [shipCompleteOnCheckout, setShipCompleteOnCheckout] = React.useState<boolean>(getShipCompleteValue());
    const shipCompleteOnCheckoutToggle = _renderShipOnCheckout(
        shipCompleteOnCheckout,
        handleShipCompleteOnCheckoutChange,
        props.resources.shipCompleteOnCheckoutLabel
    );
    const {
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        keepShoppingButton,
        config: { displayDefaultShippingLine },
        context: { cultureFormatter }
    } = props;
    const cart: Cart = Object.assign({}, props.data.checkout.result?.checkoutCart.cart);
    const updateCart: ICartState | undefined = props.data.checkout.result?.checkoutCart;
    const chargeLineToBeRemoved = props.context.app.config.chargeLineToBeRemoved || '';
    const chargeLinesBreakdown: IChargeLinesBreakdownData[] = props.context.app.config.chargeLinesBreakdown;

    let chargelines = otherChargesLines(
        cart,
        chargeLinesBreakdown,
        cultureFormatter.formatCurrency,
        displayDefaultShippingLine,
        updateCart,
        chargeLineToBeRemoved
    );
    const orderSummaryExtended: IOrderSummaryExtended = {
        ...orderSummary!,
        otherChargesLines: chargelines,
        displayDefaultShippingLine: displayDefaultShippingLine
    };
    const shippingState = props.data.checkout.result?.shippingAddress?.State;
    const lineItemsExt: ILineItemsExtended = {
        ...lineItems!,
        shippingState: shippingState
    };
    return (
        <Module {...checkoutProps}>
            {!hasSalesOrder && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {guidedForm}
                                <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                {props.config.shipCompleteOnCheckout && shipCompleteOnCheckoutToggle}
                                <Node {...mainControlProps}>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                            <Node {...sideProps}>
                                {!hasInvoiceLine
                                    ? orderSummary && <OrderSummaryComponent {...orderSummaryExtended} />
                                    : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                                <Node {...sideControlFirstProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                                {lineItems && <LineItemsComponent {...lineItemsExt} />}
                                {_renderOrderComment(getCommentHeadingText(), getCommentText())}
                                <Node {...sideControlSecondProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
